import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productsAPI, brandsAPI, categoriesAPI } from '../lib/api';
import { formatCurrency as fmtCurrency, formatDate as fmtDate } from '../lib/formatters';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../components/ui/popover';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Package, Plus, Search, Edit2, Trash2, AlertTriangle, Barcode, Boxes, Loader2, History, Tag, FolderTree, Check, X, Mail, Printer } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ImageUploader from '../components/ImageUploader';
import { alertsAPI } from '../lib/api';
import { Trans } from 'react-i18next';

const emptyProduct = {
    sku: '',
    brand_id: '',
    category_id: '',
    name: '',
    description: '',
    category: '',
    brand: '',
    unit: 'pcs',
    barcode: '',
    image_url: '',
    price: '',
    cost_price: '',
    stock_qty: '',
    low_stock_threshold: 5,
    vat_rate: 20,
    batch_required: false,
    units_per_carton: '',
    package_description: '',
};

/**
 * Compact percent badge for the per-row Margin column.
 *   green  ≥ 40%       (healthy retail margin)
 *   amber  20% – 40%   (acceptable, monitor)
 *   red    < 20%       (under-priced — admin should review)
 * Falls back to a muted "—" when cost_price is missing or non-positive,
 * since margin can't be computed in that case.
 */
function MarginBadge({ price, cost }) {
    const p = Number(price);
    const c = Number(cost);
    if (!p || !c || p <= 0) {
        return <span className="text-gray-300">—</span>;
    }
    const pct = ((p - c) / p) * 100;
    const cls =
        pct >= 40 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : pct >= 20 ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-red-50 text-red-700 border-red-200';
    return (
        <span
            className={`inline-block min-w-[58px] text-center font-mono text-xs font-semibold border rounded-md px-2 py-0.5 ${cls}`}
            title={`Vente ${p.toFixed(2)} € · Coût ${c.toFixed(2)} €`}
        >
            {pct.toFixed(0)}%
        </span>
    );
}

const Products = () => {
    const { t, i18n } = useTranslation(['entities', 'common']);
    const { isAdmin } = useAuth();
    const [products, setProducts] = useState([]);
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [brandFilter, setBrandFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyProduct);
    const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
    const [skuPreview, setSkuPreview] = useState('');
    const [saving, setSaving] = useState(false);
    const [filter, setFilter] = useState('all'); // all | low
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyProduct, setHistoryProduct] = useState(null);
    const [historyMovements, setHistoryMovements] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            if (brandFilter !== 'all') params.brand_id = brandFilter;
            if (categoryFilter !== 'all') params.category_id = categoryFilter;
            const { data } = await productsAPI.getAll(params);
            setProducts(data);
        } catch (e) {
            toast.error(t('entities:products.toasts.load_failed'));
        } finally {
            setLoading(false);
        }
    };

    const fetchBrands = async () => {
        try {
            const { data } = await brandsAPI.getAll();
            setBrands(data);
        } catch (err) {
            // Non-fatal — brand filter falls back to empty list. Log so
            // it shows up in production error tracking.
            console.error('[Products] fetchBrands failed:', err?.response?.status || err?.message);
        }
    };

    const fetchCategories = async () => {
        try {
            const { data } = await categoriesAPI.getAll();
            setCategories(data);
        } catch (err) {
            console.error('[Products] fetchCategories failed:', err?.response?.status || err?.message);
        }
    };

    useEffect(() => {
        fetchProducts();
        fetchBrands();
        fetchCategories();
    }, []);

    useEffect(() => {
        const t = setTimeout(fetchProducts, 300);
        return () => clearTimeout(t);
    }, [search, brandFilter, categoryFilter]);

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyProduct);
        setSkuPreview('');
        setOpen(true);
    };

    const openEdit = (p) => {
        setEditingId(p.id);
        setForm({ ...emptyProduct, ...p });
        setSkuPreview('');
        setOpen(true);
    };

    const handleBrandChange = async (brand_id) => {
        const brand = brands.find((b) => b.id === brand_id);
        setForm({
            ...form,
            brand_id,
            brand: brand?.name || '',
        });
        // Only auto-preview SKU when creating (not editing) and SKU field is empty
        if (!editingId && !form.sku && brand_id) {
            try {
                const { data } = await brandsAPI.nextSku(brand_id);
                setSkuPreview(data.next_sku);
            } catch {
                setSkuPreview('');
            }
        } else {
            setSkuPreview('');
        }
    };

    const handleCategorySelect = (cat) => {
        setForm({ ...form, category_id: cat.id, category: cat.name });
        setCategoryPickerOpen(false);
    };

    const handleCreateCategoryInline = async (name) => {
        const trimmed = (name || '').trim();
        if (!trimmed) return;
        try {
            const { data } = await categoriesAPI.create({ name: trimmed });
            setCategories((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
            setForm((f) => ({ ...f, category_id: data.id, category: data.name }));
            setCategoryPickerOpen(false);
            toast.success(t('entities:products.toasts.category_created', { name: data.name }));
        } catch (e) {
            toast.error(e.response?.data?.detail || t('entities:products.toasts.category_failed'));
        }
    };

    const handleSave = async () => {
        if (!form.name) {
            toast.error(t('entities:products.toasts.name_required'));
            return;
        }
        if (!form.sku && !form.brand_id) {
            toast.error(t('entities:products.toasts.sku_or_brand_required'));
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                price: parseFloat(form.price || 0),
                cost_price: parseFloat(form.cost_price || 0),
                stock_qty: parseFloat(form.stock_qty || 0),
                low_stock_threshold: parseFloat(form.low_stock_threshold || 0),
                vat_rate: parseFloat(form.vat_rate ?? 20),
                batch_required: !!form.batch_required,
                // Coerce package fields: empty string → null so backend
                // treats "unset" as "keep null" rather than "int(0)".
                units_per_carton: form.units_per_carton === '' || form.units_per_carton == null
                    ? null : parseInt(form.units_per_carton, 10),
                package_description: form.package_description || null,
            };
            if (!payload.sku) delete payload.sku;  // let backend auto-generate
            if (!payload.brand_id) delete payload.brand_id;
            if (!payload.category_id) delete payload.category_id;
            if (editingId) {
                await productsAPI.update(editingId, payload);
                toast.success(t('entities:products.toasts.updated'));
            } else {
                await productsAPI.create(payload);
                toast.success(t('entities:products.toasts.created'));
            }
            setOpen(false);
            fetchProducts();
            fetchBrands();  // refresh sequence counts
        } catch (e) {
            toast.error(e.response?.data?.detail || t('entities:products.toasts.save_failed'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('entities:products.toasts.delete_confirm'))) return;
        try {
            await productsAPI.delete(id);
            toast.success(t('entities:products.toasts.deleted'));
            fetchProducts();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('entities:products.toasts.delete_failed'));
        }
    };

    const openHistory = async (p) => {
        setHistoryProduct(p);
        setHistoryOpen(true);
        setHistoryLoading(true);
        try {
            const { data } = await productsAPI.getStockHistory(p.id);
            setHistoryMovements(data);
        } catch (e) {
            toast.error(t('entities:products.toasts.history_failed'));
        } finally {
            setHistoryLoading(false);
        }
    };

    const movementBadge = (type) => {
        const map = {
            sale: { key: 'sale', cls: 'bg-[#E76F51]/20 text-[#E76F51]' },
            manual_adjustment: { key: 'manual_adjustment', cls: 'bg-[#264653]/20 text-[#264653]' },
            stock_set: { key: 'stock_set', cls: 'bg-[#E9C46A]/20 text-[#8a6d00]' },
            initial: { key: 'initial', cls: 'bg-[#2A9D8F]/20 text-[#2A9D8F]' },
        };
        const m = map[type] || { key: null, cls: 'bg-gray-200 text-gray-800' };
        const label = m.key ? t(`entities:products.history.types.${m.key}`) : type;
        return <Badge className={`${m.cls} font-medium text-[10px]`}>{label}</Badge>;
    };

    const formatCurrency = (v) => fmtCurrency(v, i18n.language);

    const visible = products.filter((p) => {
        if (filter === 'low') {
            return Number(p.stock_qty) <= Number(p.low_stock_threshold);
        }
        return true;
    });

    const lowCount = products.filter(
        (p) => Number(p.stock_qty) <= Number(p.low_stock_threshold)
    ).length;

    const totalValue = products.reduce(
        (sum, p) => sum + Number(p.stock_qty || 0) * Number(p.price || 0),
        0
    );

    return (
        <div data-testid="products-page" className="space-y-8 animate-fadeIn">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="text-[11px] uppercase tracking-[2px] text-[#C08478] font-semibold mb-2">
                        {t('entities:products.section_label')}
                    </p>
                    <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">{t('entities:products.title')}</h1>
                    <p className="text-gray-500 mt-1">{t('entities:products.subtitle')}</p>
                </div>
                <div className="flex items-center gap-2">
                    {isAdmin && lowCount > 0 && (
                        <Button
                            onClick={async () => {
                                try {
                                    const { data } = await alertsAPI.sendLowStock();
                                    toast.success(t('common:toasts.email_sent') + ` (${data.products_count} ${t('entities:products.stats.low_stock').toLowerCase()})`);
                                } catch (err) {
                                    toast.error(err?.response?.data?.detail || t('common:toasts.email_failed'));
                                }
                            }}
                            data-testid="send-low-stock-reminder"
                            variant="outline"
                            className="border-[#E76F51] text-[#E76F51] hover:bg-[#E76F51]/10"
                        >
                            <Mail size={16} className="mr-2" /> {t('common:actions.send_reminder')}
                        </Button>
                    )}
                    <Link to="/products/barcode-assignment" data-testid="open-barcode-assignment">
                        <Button variant="outline" className="gap-2">
                            <Barcode size={16} />
                            {t('entities:products.barcode_assignment', { defaultValue: 'Barcode Assignment Mode' })}
                        </Button>
                    </Link>
                    <Link to="/products/print-labels" data-testid="open-print-labels">
                        <Button variant="outline" className="gap-2">
                            <Printer size={16} />
                            Print Labels
                        </Button>
                    </Link>
                    <Button
                        onClick={openCreate}
                        data-testid="new-product-btn"
                        className="bg-[#1A1A1A] hover:bg-[#C08478] text-white"
                    >
                        <Plus size={16} className="mr-2" /> {t('entities:products.add')}
                    </Button>
                </div>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-0 shadow-sm">
                    <CardContent className="p-5 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#C08478]/10 flex items-center justify-center">
                            <Boxes size={20} className="text-[#C08478]" />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500">{t('entities:products.stats.total')}</p>
                            <p className="text-2xl font-bold text-[#1A1A1A]">{products.length}</p>
                        </div>
                    </CardContent>
                </Card>
                <Card
                    onClick={() => setFilter(filter === 'low' ? 'all' : 'low')}
                    data-testid="low-stock-filter"
                    className={`border-0 shadow-sm cursor-pointer transition ${
                        filter === 'low' ? 'ring-2 ring-[#E76F51]' : ''
                    }`}
                >
                    <CardContent className="p-5 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#E76F51]/10 flex items-center justify-center">
                            <AlertTriangle size={20} className="text-[#E76F51]" />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500">{t('entities:products.stats.low_stock')}</p>
                            <p className="text-2xl font-bold text-[#E76F51]">{lowCount}</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                    <CardContent className="p-5 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#2A9D8F]/10 flex items-center justify-center">
                            <Package size={20} className="text-[#2A9D8F]" />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500">{t('entities:products.stats.inventory_value')}</p>
                            <p className="text-2xl font-bold text-[#1A1A1A]">{formatCurrency(totalValue)}</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card className="border-0 shadow-sm">
                <CardHeader className="space-y-4">
                    <div className="flex flex-wrap justify-between items-center gap-4">
                        <CardTitle className="flex items-center gap-2">
                            <Package className="text-[#C08478]" size={20} />
                            {filter === 'low' ? t('entities:products.low_stock_only') : t('entities:products.all')}
                            <span className="text-xs font-normal text-gray-400 ml-2">
                                ({visible.length})
                            </span>
                        </CardTitle>
                        <div className="relative w-full max-w-xs">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <Input
                                data-testid="product-search"
                                placeholder={t('entities:products.search_inline_placeholder')}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9 bg-[#F5F5F5] border-[#E5E5E5]"
                            />
                        </div>
                    </div>
                    {/* Filter row */}
                    <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[#F5F5F5]">
                        <span className="text-[10px] uppercase tracking-[2px] text-gray-400 font-semibold">
                            {t('common:actions.filter')}
                        </span>
                        <Select value={brandFilter} onValueChange={setBrandFilter}>
                            <SelectTrigger
                                data-testid="filter-brand"
                                className="w-44 h-8 bg-[#F5F5F5] text-sm"
                            >
                                <Tag size={12} className="mr-1 text-[#C08478]" />
                                <SelectValue placeholder={t('entities:products.filters.all_brands')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('entities:products.filters.all_brands')}</SelectItem>
                                {brands.map((b) => (
                                    <SelectItem key={b.id} value={b.id}>
                                        <span className="font-mono text-xs text-[#C08478] mr-2">{b.prefix}</span>
                                        {b.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                            <SelectTrigger
                                data-testid="filter-category"
                                className="w-44 h-8 bg-[#F5F5F5] text-sm"
                            >
                                <FolderTree size={12} className="mr-1 text-[#264653]" />
                                <SelectValue placeholder={t('entities:products.filters.all_categories')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('entities:products.filters.all_categories')}</SelectItem>
                                {categories.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {c.name}
                                        <span className="text-xs text-gray-400 ml-1">({c.product_count})</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {(brandFilter !== 'all' || categoryFilter !== 'all') && (
                            <Button
                                data-testid="clear-filters"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setBrandFilter('all');
                                    setCategoryFilter('all');
                                }}
                                className="h-8 text-xs text-gray-500 hover:text-[#1A1A1A]"
                            >
                                <X size={12} className="mr-1" /> {t('common:actions.clear')}
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="py-16 flex justify-center">
                            <Loader2 className="animate-spin text-[#C08478]" />
                        </div>
                    ) : visible.length === 0 ? (
                        <div className="py-16 text-center text-gray-400">
                            <Trans
                                i18nKey="entities:products.empty"
                                components={{ strong: <strong className="font-medium" /> }}
                            />
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('entities:products.columns.sku')}</TableHead>
                                    <TableHead>{t('entities:products.columns.name')}</TableHead>
                                    <TableHead>{t('entities:products.columns.category')}</TableHead>
                                    <TableHead className="text-right">{t('entities:products.columns.price')}</TableHead>
                                    {isAdmin() && (
                                        <TableHead className="text-right">
                                            {t('entities:products.columns.margin', { defaultValue: 'Margin' })}
                                        </TableHead>
                                    )}
                                    <TableHead className="text-right">{t('entities:products.columns.stock')}</TableHead>
                                    <TableHead className="text-right">{t('entities:products.columns.threshold')}</TableHead>
                                    <TableHead className="text-right">{t('entities:products.columns.actions')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {visible.map((p) => {
                                    const isLow = Number(p.stock_qty) <= Number(p.low_stock_threshold);
                                    return (
                                        <TableRow key={p.id} data-testid={`product-row-${p.sku}`}>
                                            <TableCell className="font-mono text-sm">{p.sku}</TableCell>
                                            <TableCell>
                                                <div className="font-medium">{p.name}</div>
                                                {p.brand && <div className="text-xs text-gray-400">{p.brand}</div>}
                                            </TableCell>
                                            <TableCell>{p.category || '-'}</TableCell>
                                            <TableCell className="text-right font-medium">{formatCurrency(p.price)}</TableCell>
                                            {isAdmin() && (
                                                <TableCell className="text-right">
                                                    <MarginBadge price={p.price} cost={p.cost_price} />
                                                </TableCell>
                                            )}
                                            <TableCell className="text-right">
                                                {isLow ? (
                                                    <Badge className="bg-[#E76F51]/20 text-[#E76F51] font-bold">
                                                        {p.stock_qty} {p.unit}
                                                    </Badge>
                                                ) : (
                                                    <span className="font-mono">
                                                        {p.stock_qty} {p.unit}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right text-gray-500">{p.low_stock_threshold}</TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    data-testid={`history-product-${p.sku}`}
                                                    onClick={() => openHistory(p)}
                                                    title={t('entities:products.history.tooltip')}
                                                    className="text-gray-500 hover:text-[#264653]"
                                                >
                                                    <History size={16} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    data-testid={`edit-product-${p.sku}`}
                                                    onClick={() => openEdit(p)}
                                                    className="text-gray-500 hover:text-[#C08478]"
                                                >
                                                    <Edit2 size={16} />
                                                </Button>
                                                {isAdmin() && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        data-testid={`delete-product-${p.sku}`}
                                                        onClick={() => handleDelete(p.id)}
                                                        className="text-gray-500 hover:text-red-500"
                                                    >
                                                        <Trash2 size={16} />
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingId ? t('entities:products.dialog.edit') : t('entities:products.dialog.new')}</DialogTitle>
                        <DialogDescription>
                            {editingId
                                ? t('entities:products.dialog.description_edit')
                                : t('entities:products.dialog.description_new')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>{t('entities:products.dialog.brand')}</Label>
                            {brands.length === 0 ? (
                                <div className="px-3 py-2 text-xs rounded-md bg-[#F5F5F5] text-gray-500 border border-dashed border-[#E5E5E5]">
                                    {t('entities:products.dialog.no_brands')}{' '}
                                    <Link to="/brands" className="text-[#C08478] font-medium hover:underline">
                                        {t('entities:products.dialog.create_brand')}
                                    </Link>
                                </div>
                            ) : (
                                <Select
                                    value={form.brand_id || ''}
                                    onValueChange={handleBrandChange}
                                >
                                    <SelectTrigger data-testid="product-brand-select">
                                        <SelectValue placeholder={t('entities:products.dialog.select_brand')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {brands.map((b) => (
                                            <SelectItem key={b.id} value={b.id} data-testid={`brand-opt-${b.prefix}`}>
                                                <span className="font-mono text-xs text-[#C08478] mr-2">{b.prefix}</span>
                                                {b.name}
                                                <span className="text-xs text-gray-400 ml-2">
                                                    ({t('entities:products.dialog.next_label')}: {b.next_sku_preview})
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label>
                                {t('entities:products.dialog.sku')}
                                {!editingId && form.brand_id && !form.sku && (
                                    <span className="text-xs text-gray-400 ml-2 font-normal">
                                        {t('entities:products.dialog.sku_auto_hint')}<span className="font-mono text-[#C08478]">{skuPreview || '...'}</span>)
                                    </span>
                                )}
                            </Label>
                            <Input
                                data-testid="product-sku"
                                value={form.sku}
                                onChange={(e) => setForm({ ...form, sku: e.target.value })}
                                placeholder={skuPreview || t('entities:products.dialog.sku_placeholder')}
                                className="font-mono"
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label>{t('entities:products.dialog.name_label')}</Label>
                            <Input
                                data-testid="product-name"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder={t('entities:products.dialog.name_placeholder')}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>{t('entities:products.dialog.category')}</Label>
                            <Popover open={categoryPickerOpen} onOpenChange={setCategoryPickerOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        role="combobox"
                                        data-testid="product-category-trigger"
                                        className="w-full justify-between h-10 bg-white font-normal"
                                    >
                                        <span className="flex items-center gap-2 truncate text-left">
                                            <Search size={12} className="text-gray-400 shrink-0" />
                                            {form.category || t('entities:products.dialog.category_trigger_placeholder')}
                                        </span>
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                    className="w-[--radix-popover-trigger-width] p-0"
                                    align="start"
                                >
                                    <Command>
                                        <CommandInput
                                            placeholder={t('entities:products.dialog.category_search_placeholder')}
                                            data-testid="product-category-input"
                                        />
                                        <CommandList>
                                            <CommandEmpty>
                                                <button
                                                    type="button"
                                                    data-testid="create-category-inline"
                                                    onClick={() => {
                                                        const v = document.querySelector(
                                                            '[data-testid="product-category-input"]'
                                                        )?.value;
                                                        handleCreateCategoryInline(v);
                                                    }}
                                                    className="w-full px-3 py-2 text-sm text-[#C08478] hover:bg-[#F5F5F5] flex items-center gap-2"
                                                >
                                                    <Plus size={12} />
                                                    {t('entities:products.dialog.create_category')}
                                                </button>
                                            </CommandEmpty>
                                            {form.category_id && (
                                                <CommandGroup>
                                                    <CommandItem
                                                        value="__clear__"
                                                        onSelect={() => {
                                                            setForm({ ...form, category_id: '', category: '' });
                                                            setCategoryPickerOpen(false);
                                                        }}
                                                        data-testid="clear-category"
                                                        className="text-gray-500"
                                                    >
                                                        <X size={12} className="mr-2" /> {t('entities:products.dialog.clear_selection')}
                                                    </CommandItem>
                                                </CommandGroup>
                                            )}
                                            <CommandGroup>
                                                {categories.map((c) => (
                                                    <CommandItem
                                                        key={c.id}
                                                        value={c.name}
                                                        onSelect={() => handleCategorySelect(c)}
                                                        data-testid={`category-opt-${c.id}`}
                                                    >
                                                        <Check
                                                            size={14}
                                                            className={`mr-2 ${form.category_id === c.id ? 'opacity-100' : 'opacity-0'}`}
                                                        />
                                                        {c.name}
                                                        <span className="text-xs text-gray-400 ml-auto">
                                                            {c.product_count}
                                                        </span>
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div className="space-y-2">
                            <Label>{t('entities:products.dialog.price')}</Label>
                            <Input
                                data-testid="product-price"
                                type="number"
                                step="0.01"
                                value={form.price}
                                onChange={(e) => setForm({ ...form, price: e.target.value })}
                            />
                        </div>
                        {isAdmin() && (
                            <div className="space-y-2">
                                <Label>{t('entities:products.dialog.cost_price')}</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={form.cost_price}
                                    onChange={(e) => setForm({ ...form, cost_price: e.target.value })}
                                />
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label>{t('entities:products.dialog.stock_qty')}</Label>
                            <Input
                                data-testid="product-stock"
                                type="number"
                                step="1"
                                value={form.stock_qty}
                                onChange={(e) => setForm({ ...form, stock_qty: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>{t('entities:products.dialog.low_stock_threshold')}</Label>
                            <Input
                                type="number"
                                step="1"
                                value={form.low_stock_threshold}
                                onChange={(e) =>
                                    setForm({ ...form, low_stock_threshold: e.target.value })
                                }
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>{t('entities:products.dialog.vat_rate')}</Label>
                            <Input
                                data-testid="product-vat-rate"
                                type="number"
                                step="0.1"
                                value={form.vat_rate}
                                onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
                                placeholder="20"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>{t('entities:products.dialog.unit')}</Label>
                            <Input
                                value={form.unit}
                                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                                placeholder={t('entities:products.dialog.unit_placeholder')}
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2 flex items-center justify-between rounded-md border border-[#E5E5E5] p-3">
                            <div>
                                <Label htmlFor="batch-required-toggle" className="cursor-pointer">
                                    {t('entities:products.batch_required_label')}
                                </Label>
                                <p className="text-[10px] text-gray-500 mt-1">
                                    {t('entities:products.batch_required_hint')}
                                </p>
                            </div>
                            <Switch
                                id="batch-required-toggle"
                                data-testid="batch-required-toggle"
                                checked={!!form.batch_required}
                                onCheckedChange={(checked) => setForm({ ...form, batch_required: checked })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>{t('entities:products.dialog.barcode')}</Label>
                            <Input
                                value={form.barcode}
                                onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label>{t('entities:products.dialog.image_url')}</Label>
                            <ImageUploader
                                value={form.image_url}
                                onChange={(url) => setForm({ ...form, image_url: url })}
                                kind="product"
                                placeholder={t('entities:products.dialog.image_url_placeholder')}
                                data-testid="product-image"
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label>{t('entities:products.dialog.description')}</Label>
                            <Textarea
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                rows={2}
                            />
                        </div>
                        {/* --- Package Size (new) ---------------------------
                            Structured `units_per_carton` (int) drives MOQ &
                            logistics calculations; free-text `package_description`
                            is what customers see on the product detail modal. */}
                        <div className="space-y-2">
                            <Label>{t('entities:products.dialog.units_per_carton', { defaultValue: 'Units per carton' })}</Label>
                            <Input
                                type="number"
                                min="0"
                                step="1"
                                value={form.units_per_carton}
                                onChange={(e) => setForm({ ...form, units_per_carton: e.target.value })}
                                placeholder="24"
                                data-testid="product-units-per-carton"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>{t('entities:products.dialog.package_description', { defaultValue: 'Package description' })}</Label>
                            <Input
                                value={form.package_description}
                                onChange={(e) => setForm({ ...form, package_description: e.target.value })}
                                placeholder="Carton of 24 × 200 ml"
                                data-testid="product-package-description"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            {t('common:actions.cancel')}
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            data-testid="save-product"
                            className="bg-[#1A1A1A] hover:bg-[#C08478] text-white"
                        >
                            {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
                            {editingId ? t('common:actions.update') : t('common:actions.create')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Stock History Modal */}
            <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="stock-history-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            {t('entities:products.history.title')} — {historyProduct?.name}
                            {historyProduct?.sku && (
                                <span className="text-xs font-mono text-gray-400 ml-2">{historyProduct.sku}</span>
                            )}
                        </DialogTitle>
                        <DialogDescription>
                            {t('entities:products.history.description')}
                        </DialogDescription>
                    </DialogHeader>
                    {historyLoading ? (
                        <div className="py-10 flex justify-center">
                            <Loader2 className="animate-spin text-[#C08478]" />
                        </div>
                    ) : historyMovements.length === 0 ? (
                        <div className="py-10 text-center text-gray-400">{t('entities:products.history.empty')}</div>
                    ) : (
                        <div className="max-h-[60vh] overflow-y-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-[#F5F5F5]">
                                        <TableHead>{t('entities:products.history.columns.type')}</TableHead>
                                        <TableHead className="text-right">{t('entities:products.history.columns.delta')}</TableHead>
                                        <TableHead className="text-right">{t('entities:products.history.columns.balance_after')}</TableHead>
                                        <TableHead>{t('entities:products.history.columns.reason')}</TableHead>
                                        <TableHead>{t('entities:products.history.columns.user')}</TableHead>
                                        <TableHead>{t('entities:products.history.columns.when')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {historyMovements.map((m) => (
                                        <TableRow key={m.id} data-testid={`movement-${m.id}`}>
                                            <TableCell>{movementBadge(m.type)}</TableCell>
                                            <TableCell className={`text-right font-mono font-semibold ${m.delta < 0 ? 'text-[#E76F51]' : 'text-[#2A9D8F]'}`}>
                                                {m.delta > 0 ? '+' : ''}{m.delta}
                                            </TableCell>
                                            <TableCell className="text-right font-mono">{m.balance_after}</TableCell>
                                            <TableCell className="text-sm text-gray-600">
                                                {m.reason || '-'}
                                                {m.ref_invoice_number && (
                                                    <div className="text-[10px] font-mono text-[#C08478]">{m.ref_invoice_number}</div>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-sm text-gray-600">{m.user_name}</TableCell>
                                            <TableCell className="text-xs text-gray-500">
                                                {fmtDate(m.created_at, i18n.language)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Products;
